// =====================================================================
// addRelativeUniversal — single source of truth for adding a relative.
// =====================================================================
//
// This is the ONE function every "Add a relative" surface calls. It
// handles EVERY relationship mode end-to-end with the SAME pipeline:
//
//   1. Validate the only HARD requirement: a non-empty name.
//   2. Insert the person row (addMember).
//   3. Build a freshly-merged local snapshot that contains the just-
//      confirmed `created` row on top of deps.members (so post-insert
//      ancestor / existing-parent / connector lookups never read a stale
//      React closure).
//   4. Resolve the relationship edge intent and APPEND it (never replace
//      existing parent_ids). Set-deduped append everywhere.
//   5. Verify against the locally-computed expected state — never against
//      a stale deps.members reference re-read after refresh.
//   6. Refresh — pulls the new row + every updated parent_ids back from
//      the DB so the All Relatives roster + tree update immediately.
//   7. Return a structured result with explicit per-step success/failure
//      so silent failures become visible diagnostics. If a relationship
//      has no possible structural edge (e.g. sibling with no parents
//      anywhere yet), we surface a clear edgeError explaining what to
//      pick — the row STILL persists so the user never loses it.
//
// SCHEMA — only two structural edges exist on the database:
//   • parent_ids  (parent → child) — the source of truth for ancestry.
//   • spouse_id   (optional partner display layer).
// Sibling / aunt-uncle / cousin / niece-nephew / grandchild are all
// derived from parent_ids walks (see `computeKinship` in
// FamilyGraphContext). This file NEVER invents new edge columns.
//
// ─── PER-RELATIONSHIP CONTRACT ────────────────────────────────────────
//
//   Father / Mother / Step-Parent / Adopted-Parent
//     viewer-parent mode → APPEND created.id to viewer.parent_ids.
//
//   Grandparent / Great-Grandparent
//     connector-parent mode → APPEND created.id to connector's parent_ids.
//
//   Sibling (incl. Half-Sibling display override)
//     • Anchor picked → new row's parent_ids = [anchor, secondAnchor?].
//     • No anchor, viewer has parents → inherit viewer.parent_ids.
//     • Neither → row saves floating with explicit "pick a parent" edge
//       error. Caller can re-link from the roster.
//
//   Aunt / Uncle (incl. Great-Aunt/Uncle override)
//     • Anchor with parents → inherit anchor.parent_ids.
//     • Anchor without parents → save floating with explicit
//       "anchor has no recorded parents yet" edge error.
//     • No anchor → save floating with explicit "pick a parent" edge
//       error.
//
//   Cousin / Cousin-Child (incl. Niece/Nephew override)
//     • Anchor picked → new row's parent_ids = [anchor, secondAnchor?].
//     • No anchor → save floating with explicit edge error.
//
//   Child (incl. Grandchild / Great-Grandchild display override)
//     • Anchor picked → new row's parent_ids = [anchor, secondAnchor?].
//     • No anchor, viewer exists → new row's parent_ids = [viewer.id].
//     • Neither → save floating with explicit "no parent available"
//       edge error.
//
//   Spouse / Partner (incl. In-law display overrides)
//     • Anchor picked → spouse_id link via linkSpouses(anchor, created).
//     • No anchor → save floating with explicit "pick a partner" edge
//       error.
//
//   Unknown / Other / Free-form chosen-family labels
//     Save floating with neutral edge error so the row appears in the
//     roster — the user can attach later via the per-card menu.
//
// FAILURE CONTRACT:
//   • Person insert fails  → success=false, personError set, no edge
//     attempt. Row is NOT in the DB.
//   • Person insert OK, edge fails to write → success=true (row IS in
//     the DB), edgeError set with the verbatim DB error or a clear
//     "needs anchor" reason.
//   • Person insert OK, edge intentionally floating (no anchor available
//     at all) → success=true, edgeError populated with a friendly
//     "pick a parent / partner" hint so the user can finish the link.
//   • Any of these: result.modeApplied tells the diagnostic panel which
//     code path actually ran (viewer-parent / connector-parent / self /
//     spouse / floating / none).
//
// PURE INTEGRATION HELPER — no schema change, no DB writes the existing
// FamilyGraphContext methods don't already do. parent_ids semantics,
// billing, FamousPay, auth, invites, uploads, Resend, onboarding,
// homepage, pricing, and email settings are NEVER touched.

import type {
  GraphMember,
  Relationship,
  Side,
  AddMemberInput,
  TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';

import {
  resolveCanonicalTreeRelationshipCreationPolicy,
} from '@/governor/tree/CanonicalTreeRelationshipCreationPolicy';

import {
  resolveCanonicalTreeGenerationAnchorAuthority,
} from '@/governor/tree/CanonicalTreeTopologyAuthority';

import {
  planCanonicalTreeAncestryReconnection,
} from '@/governor/tree/CanonicalTreeAncestryReconnectionAuthority';


export interface AddRelativeParams {
  name: string;
  relationship: Relationship;
  /**
   * Canonical extended relationship option key.
   *
   * Required to distinguish display depths that share one structural
   * relationship enum, such as Great-Grandparent vs Great-Great-Grandparent.
   */
  optionKey?: string | null;
  /** Anchor relative this new person connects through. See module header. */
  anchorId?: string | null;
  /** Optional secondary parent (dual-parent links for child/sibling). */
  secondAnchorId?: string | null;
  side?: Side;
  /** Free-form label override (e.g. "Step-Mom", "Godmother"). */
  relationshipOverride?: string | null;
  /** Governed modern/chosen-family workflow descriptor. */
  relationshipWorkflow?: TreeRelationshipWorkflow | null;
  // Optional profile detail passthrough (forwarded to addMember).
  birthYear?: number | null;
  birthDate?: string | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  livingStatus?: 'living' | 'deceased' | null;
  notes?: string | null;
  city?: string | null;
  description?: string | null;
  photoUrl?: string | null;
  approvalStatus?: 'pending' | 'approved';
}

/** Discriminator describing which structural edge path actually ran. */
export type AppliedEdgeMode =
  | 'viewer-parent'
  | 'connector-parent'
  | 'co-parent-child'
  | 'workflow-lateral'
  | 'self'
  | 'spouse'
  | 'floating'
  | 'none';

export interface SpouseSaveDiagnostics {
  /** True once the new person row was confirmed in the DB (addMember returned). */
  personCreated: boolean;
  /** True once spouse_id was written on BOTH sides (anchor + new row). */
  spouseEdgeCreated: boolean;
  /** True when the picked spouse anchor was found in the loaded graph. */
  spouseAnchorFound: boolean;
  /** True once layout will render the new card next to the spouse anchor.
   *  Trusted as `true` whenever the spouse edge wrote successfully — the
   *  pyramid layout's spouse-anchored tier inheritance + couple unit
   *  builder produce the side-by-side render automatically. */
  renderedNextToSpouse: boolean;
  /** Always true for the spouse path — parents are NOT required. */
  noParentRequired: boolean;
  /** Echo of the spouse anchor id resolved from the form. */
  spouseAnchorId: string | null;
  /** Display name of the spouse anchor (for the visible diag panel). */
  spouseAnchorName: string | null;
  /** Pre-insert duplicate scan complete (always true once we reach the
   *  spouse branch — populated even when no duplicate was found so the
   *  user sees the check ran). */
  duplicateCheckComplete: boolean;
  /** True when an existing member with matching name + same spouse anchor
   *  was found and we reused it instead of creating a new row. */
  duplicateFound: boolean;
  /** True once a verifiable spouse_id link exists between the anchor and
   *  this person (post-write or pre-existing). Implies "removed from
   *  Needs Connection" because the layout's spouse-anchored tier
   *  inheritance picks them up the moment the reciprocal link lands. */
  spouseEdgeExists: boolean;
  /** True once the row will no longer appear in the bottom Needs Connection
   *  overflow. Mirrors `spouseEdgeExists` but is named for the operator-
   *  facing checklist in the visible diagnostic panel. */
  removedFromNeedsConnection: boolean;
}


export interface AddRelativeResult {
  /** True when the person row was successfully written to the DB. */
  success: boolean;
  /** The newly-created member, when success === true. */
  member: GraphMember | null;
  /** Person-row insert error message, when person insert failed. */
  personError: string | null;
  /** Edge write error message, when person insert succeeded but edge failed
   *  OR no anchor was available to form a structural edge. */
  edgeError: string | null;
  /** The raw DB error message (preferred by the diagnostic panel). */
  databaseError: string | null;
  /** Echo of the relationship the user picked. */
  relationship: Relationship;
  /** Echo of the resolved anchor id (viewer-fallback applied). */
  anchorId: string | null;
  /** The current viewer member id at the time of save. */
  viewerId: string | null;
  /** The auth.users.id of the logged-in user at the time of save. */
  userId: string | null;
  /** parent_ids the new row was inserted with (or [] when none). */
  parentIdsApplied: string[];
  /** Which structural edge path actually ran. */
  modeApplied: AppliedEdgeMode;
  /** True ONLY when the structural edge is verifiably present locally. */
  edgeVerified: boolean;
  /**
   * Spouse-mode-only diagnostic trace. Populated ONLY when modeApplied
   * === 'spouse'. Surfaced verbatim by the modal's diagnostic panel so
   * the user can see exactly which spouse-specific step succeeded.
   */
  spouseDiagnostics?: SpouseSaveDiagnostics | null;
  /** ISO timestamp of the attempt. */
  timestamp: string;
}


export interface ConnectExistingAncestryAtomicRequest {
  viewerMemberId: string;
  relationship: Relationship;
  requestedSide: Side | null;
  anchorMemberId: string | null;
  secondAnchorMemberId: string | null;

  edgeMemberId: string;
  evidenceMemberId: string;

  expectedParentIds: string[];
  proposedParentIds: string[];

  expectedRelationshipWorkflow:
    TreeRelationshipWorkflow | null;

  relationshipWorkflow:
    TreeRelationshipWorkflow | null;

  expectedRelationshipOverride:
    string | null;

  relationshipOverride:
    string | null;

  relationshipKey: string;

  modeApplied:
    | 'viewer-parent'
    | 'connector-parent'
    | 'self';
}

export interface ConnectExistingAncestryAtomicResult {
  status: string;
  edgeMemberId: string;
  evidenceMemberId: string;

  parentIds: string[];

  relationshipWorkflow:
    TreeRelationshipWorkflow | null;

  relationshipOverride:
    string | null;

  auditId: string;
}

export interface AddRelativeDeps {
  addMember: (input: AddMemberInput) => Promise<GraphMember | null>;
  updateMember?: (
    memberId: string,
    updates: Partial<GraphMember>,
  ) => Promise<void>;
  connectExistingAncestryAtomic?: (
    request: ConnectExistingAncestryAtomicRequest,
  ) => Promise<ConnectExistingAncestryAtomicResult>;
  linkParents: (memberId: string, parentIds: string[]) => Promise<void>;
  linkSpouses: (aId: string, bId: string | null) => Promise<void>;
  rollbackCreatedMember: (
    member: GraphMember,
    reason?: string | null,
  ) => Promise<void>;
  refresh: () => Promise<void>;
  members: GraphMember[];
  viewerMemberId: string | null;
  /** auth.users.id (optional — only used for the diagnostic echo). */
  userId?: string | null;
}

/**
 * Resolve the edge intent. Pure helper — never throws, never mutates.
 *
 * Returns one of:
 *   { mode: 'viewer-parent' }                  → APPEND created.id to viewer.parent_ids.
 *   { mode: 'connector-parent', connectorId,
 *     existingParents }                        → APPEND created.id to connector.parent_ids.
 *   { mode: 'co-parent-child', childId }        → APPEND created.id to the shared child's parent_ids.
 *   { mode: 'self', parents }                  → INSERT created with parent_ids = parents.
 *   { mode: 'spouse', spouseId }               → spouse_id link only.
 *   { mode: 'floating', reason }               → no anchor available — row
 *                                                saves visible-but-unattached.
 */
type ResolvedEdge =
  | { mode: 'viewer-parent' }
  | { mode: 'connector-parent'; connectorId: string; existingParents: string[] }
  | { mode: 'co-parent-child'; childId: string }
  | { mode: 'self'; parents: string[] }
  | { mode: 'spouse'; spouseId: string }
  | { mode: 'floating'; reason: string };

const resolveEdge = (
  members: GraphMember[],
  viewerMemberId: string | null,
  relationship: Relationship,
  anchorId: string | null,
  secondAnchorId: string | null,
): ResolvedEdge => {
  const byId = new Map(members.map((m) => [m.id, m]));
  const anchor = anchorId ? byId.get(anchorId) ?? null : null;
  const secondAnchor =
    secondAnchorId && secondAnchorId !== anchorId
      ? byId.get(secondAnchorId) ?? null
      : null;
  const viewer = viewerMemberId ? byId.get(viewerMemberId) ?? null : null;

  switch (relationship) {
    case 'mother':
    case 'father':
    case 'step-parent':
    case 'adopted-parent': {
      if (!viewer) {
        return {
          mode: 'floating',
          reason:
            'Could not find your "You" profile to attach this parent to. Open your own card and confirm "This is me" first, then add them again.',
        };
      }
      return { mode: 'viewer-parent' };
    }

    case 'grandparent':
    case 'great-grandparent': {
      if (!anchor) {
        return {
          mode: 'floating',
          reason:
            'Pick which parent this grandparent belongs to so we can connect them — or attach later from their card.',
        };
      }
      const existing = (anchor.parentIds ?? []).filter(Boolean);
      return {
        mode: 'connector-parent',
        connectorId: anchor.id,
        existingParents: existing,
      };
    }

    case 'sibling': {
      // Sibling: prefer explicit anchor pick (mom/dad), then dual anchor,
      // then viewer's parents, otherwise float with a clear hint.
      if (anchor) {
        const parents: string[] = [anchor.id];
        if (secondAnchor) parents.push(secondAnchor.id);
        return { mode: 'self', parents };
      }
      if (viewer) {
        const viewerParents = (viewer.parentIds ?? []).filter(Boolean);
        if (viewerParents.length > 0) {
          return { mode: 'self', parents: viewerParents };
        }
      }
      return {
        mode: 'floating',
        reason:
          'No shared parents linked yet — add a parent (mom or dad) first, OR pick which parent this sibling shares with you, and we\'ll connect them.',
      };
    }

    case 'aunt-uncle': {
      if (!anchor) {
        return {
          mode: 'floating',
          reason:
            'Pick which parent or grandparent this aunt/uncle is the sibling of so we can place them on the right side.',
        };
      }

      // Aunt/uncle authority:
      // The new person is a SIBLING of the selected connector, never the
      // connector's child. Therefore they inherit the connector's parents.
      //
      // Viewer parent connector     -> aunt / uncle
      // Viewer grandparent connector -> great-aunt / great-uncle
      const sharedParents =
        (anchor.parentIds ?? []).filter(Boolean);

      if (sharedParents.length === 0) {
        return {
          mode: 'floating',
          reason:
            'The selected parent or grandparent has no parents connected yet. Add that older generation first so this sibling relationship can be proved.',
        };
      }

      return {
        mode: 'self',
        parents: Array.from(
          new Set(sharedParents),
        ),
      };
    }


    case 'cousin':
    case 'cousin-child': {
      if (!anchor) {
        return {
          mode: 'floating',
          reason:
            'Pick which aunt/uncle (for a cousin) or which cousin (for a niece/nephew) this person is the child of.',
        };
      }
      const parents = [anchor.id];
      if (secondAnchor) parents.push(secondAnchor.id);
      return { mode: 'self', parents };
    }

    case 'child': {
      const primary = anchor ?? viewer;
      if (!primary) {
        return {
          mode: 'floating',
          reason:
            'Could not find a parent to attach this child to — open your own card and confirm "This is me" so we know the parent is you, OR pick the parent from the dropdown.',
        };
      }
      const parents = [primary.id];
      if (secondAnchor) parents.push(secondAnchor.id);
      return { mode: 'self', parents };
    }

    case 'co-parent': {
      if (!anchor) {
        return {
          mode: 'floating',
          reason:
            'Pick the shared child so this co-parent can be attached through the child\'s parent relationship.',
        };
      }

      return {
        mode: 'co-parent-child',
        childId: anchor.id,
      };
    }

    case 'spouse': {
      if (!anchor) {
        return {
          mode: 'floating',
          reason:
            'Pick which existing relative this person is married to / partnered with so we can link them.',
        };
      }
      return { mode: 'spouse', spouseId: anchor.id };
    }

    case 'unknown':
    default: {
      // Truly free-form / chosen-family — save the row, no structural
      // edge. The roster shows them in "Needs connection". The relationship
      // label / override is preserved on the row.
      return {
        mode: 'floating',
        reason:
          'Saved without a structural connection — open the card later and use "Set relationship" to attach them.',
      };
    }
  }
};

/**
 * Universal entry point for adding a relative. Every save surface calls
 * this and renders its diagnostic result.
 */
export const addRelativeUniversal = async (
  deps: AddRelativeDeps,
  params: AddRelativeParams,
): Promise<AddRelativeResult> => {
  const trimmedName = (params.name ?? '').trim();
  const timestamp = new Date().toISOString();
  const result: AddRelativeResult = {
    success: false,
    member: null,
    personError: null,
    edgeError: null,
    databaseError: null,
    relationship: params.relationship,
    anchorId: params.anchorId ?? null,
    viewerId: deps.viewerMemberId ?? null,
    userId: deps.userId ?? null,
    parentIdsApplied: [],
    modeApplied: 'none',
    edgeVerified: false,
    timestamp,
  };

  // 1. Hard validation.
  if (!trimmedName) {
    result.personError = 'Name is required.';
    result.databaseError = 'Name is required.';
    return result;
  }

  /*
   * CC-TREE-LIVE-003G3A
   *
   * The universal writer is the enforcement boundary.
   * UI candidate filtering is presentation assistance, never mutation
   * authority.
   */
  const creationPolicy =
    resolveCanonicalTreeRelationshipCreationPolicy(
      params.optionKey ??
        params.relationship,
    );

  if (
    creationPolicy.disposition !==
    'CREATE'
  ) {
    result.personError =
      creationPolicy.reason;
    result.databaseError =
      creationPolicy.reason;
    return result;
  }

  /*
   * CC-TREE-LIVE-003G3G
   *
   * Canonical workflow-only lateral authority.
   *
   * SIBLING_OF is relationship evidence without ancestry mutation.
   * A valid lateral workflow must identify an existing live endpoint,
   * inherit that endpoint's generation and branch, and explicitly state
   * that it creates no ancestry.
   *
   * Stale/missing endpoints fail CLOSED before person insertion.
   */
  const candidateLateralWorkflow =
    params.relationshipWorkflow ?? null;

  const isExplicitLateralWorkflow =
    candidateLateralWorkflow?.kind ===
      'structural' &&
    candidateLateralWorkflow
      .createsAncestry === false &&
    candidateLateralWorkflow
      .inheritsGeneration === true &&
    candidateLateralWorkflow
      .inheritsBranch === true;

  const lateralWorkflowEndpointId =
    isExplicitLateralWorkflow &&
    typeof candidateLateralWorkflow
      ?.connectorMemberId === 'string' &&
    candidateLateralWorkflow
      .connectorMemberId.trim().length > 0
      ? candidateLateralWorkflow
          .connectorMemberId.trim()
      : null;

  let lateralWorkflowEndpoint:
    GraphMember | null = null;

  if (isExplicitLateralWorkflow) {
    lateralWorkflowEndpoint =
      lateralWorkflowEndpointId
        ? deps.members.find(
            member =>
              member.id ===
                lateralWorkflowEndpointId &&
              member.approvalStatus !==
                'rejected',
          ) ?? null
        : null;

    if (!lateralWorkflowEndpoint) {
      result.personError =
        'The selected sibling endpoint is no longer available. Reopen the relationship picker and select the relative again.';

      result.databaseError =
        result.personError;

      return result;
    }

    /*
     * Diagnostics echo canonical endpoint authority even when no legacy
     * structural anchor was supplied separately.
     */
    result.anchorId =
      lateralWorkflowEndpointId;
  }

  /*
   * Branch/side for an explicit sibling workflow belongs to the
   * validated endpoint, never to caller-supplied compatibility state.
   *
   * This is enforced again at the universal writer boundary so a stale
   * or manually changed UI side cannot manufacture branch placement.
   */
  const effectiveSide =
    lateralWorkflowEndpoint?.side ??
    params.side ??
    'self';

  /*
   * Canonical lateral branch authority is enforced before every
   * downstream generation, edge, and insert consumer.
   *
   * Rebind only this function's local parameter. The caller object is
   * untouched while historical downstream source anchors continue to
   * consume params.side exactly as before.
   */
  if (
    lateralWorkflowEndpoint &&
    params.side !== effectiveSide
  ) {
    params = {
      ...params,
      side: effectiveSide,
    };
  }

  const generationAnchorDecision =
    resolveCanonicalTreeGenerationAnchorAuthority({
      members:
        deps.members,
      viewerMemberId:
        deps.viewerMemberId,
      relationship:
        params.relationship,
      optionKey:
        params.optionKey ?? null,
      relationshipOverride:
        params.relationshipOverride ??
        null,
      preferredFamilyTitle:
        params.relationshipWorkflow
          ?.preferredFamilyTitle ??
        null,
      anchorMemberId:
        params.anchorId ?? null,
      side:
        params.side ?? null,
      allowMissingAnchor:
        true,
    });

  if (
    generationAnchorDecision.applies &&
    !generationAnchorDecision.authorized
  ) {
    result.personError =
      generationAnchorDecision.reason;
    result.databaseError =
      generationAnchorDecision.reason;
    return result;
  }

  // 2. Resolve edge intent against the (still-stale) deps.members. We
  //    re-resolve a few fields below using mergedMembers AFTER addMember
  //    returns so the just-confirmed row participates in lookups.
  const edgePre = resolveEdge(
    deps.members,
    deps.viewerMemberId,
    params.relationship,
    params.anchorId ?? null,
    params.secondAnchorId ?? null,
  );

  // 3. Compose the addMember insert payload. parent_ids are baked
  //    DIRECTLY into the insert when mode === 'self' so the row hits the
  //    database with the correct edge in one shot — no race window.
  const insertParentIds: string[] | undefined =
    edgePre.mode === 'self' && edgePre.parents.length > 0
      ? Array.from(new Set(edgePre.parents.filter(Boolean)))
      : undefined;
  result.parentIdsApplied = insertParentIds ?? [];

  const insertInput: AddMemberInput = {
    name: trimmedName,
    relationship: params.relationship,
    side: params.side ?? 'self',
    photoUrl: params.photoUrl ?? null,
    birthYear: params.birthYear ?? null,
    birthDate: params.birthDate ?? null,
    birthMonth: params.birthMonth ?? null,
    birthDay: params.birthDay ?? null,
    livingStatus: params.livingStatus ?? null,
    notes: params.notes ?? null,
    city: params.city ?? null,
    description: params.description ?? null,
    approvalStatus: params.approvalStatus ?? 'pending',
    parentIds: insertParentIds,
    relationshipOverride: params.relationshipOverride ?? null,
    relationshipWorkflow: params.relationshipWorkflow ?? null,
  };

  // 4. INSERT THE PERSON ROW.
  let created: GraphMember | null = null;
  try {
    created = await deps.addMember(insertInput);
    if (!created) {
      result.personError = `Could not save ${trimmedName} — the database did not confirm the new row.`;
      result.databaseError = result.personError;
      return result;
    }
  } catch (err: any) {
    const msg =
      err?.message ||
      err?.error_description ||
      err?.toString?.() ||
      'Unknown database error while saving the person record.';
    result.personError = msg;
    result.databaseError = msg;
    return result;
  }

  // Person row is in the DB.
  result.success = true;
  result.member = created;

  // 5. APPLY THE RELATIONSHIP EDGE.
  //
  // Build a freshly-merged local members snapshot that overlays the
  // just-confirmed `created` row on top of deps.members. Every read
  // below uses this snapshot — never the stale React closure.
  const mergedMembers: GraphMember[] = (() => {
    const map = new Map<string, GraphMember>(deps.members.map((m) => [m.id, m]));
    map.set(created.id, created);
    return Array.from(map.values());
  })();
  const mergedById = new Map<string, GraphMember>(
    mergedMembers.map((m) => [m.id, m]),
  );

  // Re-resolve edge against merged snapshot (so anchors/viewer that
  // include the freshly-inserted row participate in the decision).
  const edge = resolveEdge(
    mergedMembers,
    deps.viewerMemberId,
    params.relationship,
    params.anchorId ?? null,
    params.secondAnchorId ?? null,
  );

  // Helper — safe APPEND of an id to an existing parents array via
  // Set-dedup. NEVER replaces. Drops only positively-rejected ids.
  const appendParentId = (
    targetMember: GraphMember,
    newParentId: string,
  ): string[] => {
    const existing = (targetMember.parentIds ?? []).filter((p) => {
      if (!p) return false;
      if (p === newParentId) return false; // de-dup
      const row = mergedById.get(p);
      if (row && row.approvalStatus === 'rejected') return false;
      return true; // preserve known-good AND unknown ids
    });
    return Array.from(new Set([newParentId, ...existing]));
  };

  try {
    if (lateralWorkflowEndpointId) {
      /*
       * relationshipWorkflow was persisted with the person INSERT.
       *
       * Verification is therefore the DB-confirmed workflow returned on
       * the created row. No parent_ids mutation is required or permitted.
       */
      const persistedWorkflow =
        created.relationshipWorkflow;

      const workflowPersisted =
        persistedWorkflow?.kind ===
          'structural' &&
        persistedWorkflow
          .createsAncestry === false &&
        persistedWorkflow
          .inheritsGeneration === true &&
        persistedWorkflow
          .inheritsBranch === true &&
        persistedWorkflow
          .connectorMemberId ===
            lateralWorkflowEndpointId &&
        lateralWorkflowEndpoint !== null &&
        created.side ===
          lateralWorkflowEndpoint.side;

      result.modeApplied =
        'workflow-lateral';

      result.parentIdsApplied = [];

      result.edgeVerified =
        workflowPersisted;

      if (!workflowPersisted) {
        result.edgeError =
          'The person was saved, but the canonical sibling workflow was not confirmed on the saved record.';

        result.databaseError =
          result.edgeError;
      }
    } else if (edge.mode === 'viewer-parent') {
      result.modeApplied = 'viewer-parent';
      const viewer = deps.viewerMemberId
        ? mergedById.get(deps.viewerMemberId) ?? null
        : null;
      if (!viewer) {
        result.edgeError =
          'Could not find your "You" profile to attach this parent to.';
        result.databaseError = result.edgeError;
      } else {
        const nextParents = appendParentId(viewer, created.id);
        await deps.linkParents(viewer.id, nextParents);
        result.parentIdsApplied = nextParents;
        result.edgeVerified = nextParents.includes(created.id);
      }
    } else if (edge.mode === 'connector-parent') {
      result.modeApplied = 'connector-parent';
      // PRESERVE the connector's existing parent_ids — append, never replace.
      const connectorRow = mergedById.get(edge.connectorId) ?? null;
      const existing = connectorRow
        ? (connectorRow.parentIds ?? []).filter(Boolean)
        : edge.existingParents.filter(Boolean);
      const nextParents = Array.from(new Set([created.id, ...existing]));
      await deps.linkParents(edge.connectorId, nextParents);
      result.parentIdsApplied = nextParents;
      result.edgeVerified = nextParents.includes(created.id);
    } else if (edge.mode === 'co-parent-child') {
      result.modeApplied = 'co-parent-child';

      const sharedChild =
        mergedById.get(edge.childId) ?? null;

      if (!sharedChild) {
        result.edgeError =
          'The selected shared child could not be found.';
        result.databaseError = result.edgeError;
      } else {
        const nextParents =
          appendParentId(sharedChild, created.id);

        await deps.linkParents(
          sharedChild.id,
          nextParents,
        );

        result.parentIdsApplied = nextParents;
        result.edgeVerified =
          nextParents.includes(created.id);
      }

    } else if (edge.mode === 'spouse') {
      // ── SPOUSE / IN-LAW PATH ──
      // Parent-free spouse linkage with PRE-INSERT duplicate scan so the
      // same person can never be added twice for the same spouse anchor.
      // The dedupe check matches case-insensitively on trimmed name AND
      // verifies the existing row either already points at the anchor via
      // spouse_id OR has no spouse_id yet and the anchor's spouse_id
      // points back at them (or is null). When matched, we DELETE the
      // freshly-inserted `created` row and reuse the existing one.
      result.modeApplied = 'spouse';
      const spouseAnchor = mergedById.get(edge.spouseId) ?? null;
      const normName = trimmedName.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const duplicate = mergedMembers.find((m) => {
        if (m.id === created.id) return false;
        if (m.approvalStatus === 'rejected') return false;
        const mn = (m.name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        if (mn !== normName) return false;
        const linkedToAnchor =
          m.spouseId === edge.spouseId ||
          (spouseAnchor?.spouseId ?? null) === m.id ||
          ((m.spouseId ?? null) === null && (spouseAnchor?.spouseId ?? null) === null);
        return linkedToAnchor;
      });
      const diag: SpouseSaveDiagnostics = {
        personCreated: true,
        spouseAnchorFound: !!spouseAnchor,
        spouseAnchorId: edge.spouseId,
        spouseAnchorName: spouseAnchor?.name ?? null,
        spouseEdgeCreated: false,
        renderedNextToSpouse: false,
        noParentRequired: true,
        duplicateCheckComplete: true,
        duplicateFound: !!duplicate,
        spouseEdgeExists: false,
        removedFromNeedsConnection: false,
      };
      let edgeTargetId = created.id;
      if (duplicate) {
        // Reuse the existing row. The freshly inserted duplicate is
        // removed through the governed, non-interactive rollback boundary.
        // Failure remains non-fatal because the existing row is canonical,
        // but the diagnostic now records the cleanup failure.
        try {
          await deps.rollbackCreatedMember(
            created,
            'Rolled back duplicate spouse profile created during add-relative',
          );
        } catch (rollbackError: any) {
          const rollbackMessage =
            rollbackError?.message ||
            rollbackError?.toString?.() ||
            'Unknown governed rollback failure.';

          result.databaseError =
            result.databaseError ??
            rollbackMessage;

          console.warn(
            '[addRelativeUniversal] governed duplicate rollback failed',
            {
              createdId:
                created.id,
              canonicalId:
                duplicate.id,
              error:
                rollbackMessage,
            },
          );
        }
        edgeTargetId = duplicate.id;
        result.member = duplicate;
      }
      try {
        await deps.linkSpouses(edge.spouseId, edgeTargetId);
        diag.spouseEdgeCreated = true;
        diag.spouseEdgeExists = true;
        diag.renderedNextToSpouse = true;
        diag.removedFromNeedsConnection = true;
        result.edgeVerified = true;
      } catch (spouseErr: any) {
        const msg =
          spouseErr?.message ||
          spouseErr?.error_description ||
          spouseErr?.toString?.() ||
          'Unknown error while writing the spouse edge.';
        result.edgeError = `[spouse] ${msg}`;
        result.databaseError = msg;
        result.edgeVerified = false;
      }
      result.spouseDiagnostics = diag;
      // eslint-disable-next-line no-console
      console.debug('[addRelativeUniversal] spouse save diagnostics', diag);

    } else if (edge.mode === 'self') {
      result.modeApplied = 'self';

      // Defensive retry — if Supabase stripped parent_ids on insert, or
      // the insert payload didn't include them (shouldn't happen, but
      // belt-and-braces), re-apply via linkParents using the DB-confirmed
      // `created` row's actual parent_ids vs. what we intended.
      const intended = insertParentIds ?? edge.parents;
      const insertedParents = Array.isArray(created.parentIds)
        ? created.parentIds
        : [];
      const allPresent =
        intended.length === 0 ||
        intended.every((pid) => insertedParents.includes(pid));
      if (!allPresent && intended.length > 0) {
        // Build the merged target parent_ids — APPEND intended onto any
        // existing parents the row may have gained between insert and now.
        const mergedTargetParents = Array.from(
          new Set([...intended, ...insertedParents]),
        );
        await deps.linkParents(created.id, mergedTargetParents);
        result.parentIdsApplied = mergedTargetParents;
        result.edgeVerified = intended.every((p) =>
          mergedTargetParents.includes(p),
        );
      } else {
        result.parentIdsApplied = insertedParents.length > 0
          ? insertedParents
          : intended;
        result.edgeVerified = intended.length === 0
          ? true
          : intended.every((p) => insertedParents.includes(p));
      }
    } else if (edge.mode === 'floating') {
      // Row saved, no structural edge possible. Surface the reason so the
      // user knows what to pick. parent_ids stays as the DB row holds it
      // (typically empty), so the row appears in the "Needs connection"
      // tier of the roster — never lost.
      result.modeApplied = 'floating';
      result.edgeError = edge.reason;
      result.parentIdsApplied = Array.isArray(created.parentIds)
        ? created.parentIds
        : [];
      result.edgeVerified = false;
    }
  } catch (err: any) {
    const msg =
      err?.message ||
      err?.error_description ||
      err?.toString?.() ||
      `Unknown database error while writing the ${params.relationship} edge.`;
    result.edgeError = `[${params.relationship}] ${msg}`;
    result.databaseError = msg;
  }

  // 6. REFRESH — pull the newly-saved row + every updated parent_ids
  //    edge back from the DB so the All Relatives roster + tree update
  //    immediately. Refresh failures are non-fatal.
  try {
    await deps.refresh();
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.warn('[addRelativeUniversal] post-save refresh failed (non-fatal)', err);
  }

  return result;
};

// =====================================================================
// connectExistingUniversal — same edge contract as addRelativeUniversal,
// but applied to an EXISTING person row instead of inserting a new one.
// =====================================================================
//
// Used by the duplicate-detection "Connect" button so picking an existing
// profile actually creates the relationship edge to the viewer's family
// tree — never just relabels the row and silently leaves it floating.
//
// Same per-mode contract as addRelativeUniversal:
//   • Father / Mother / Step / Adopted → APPEND target.id to viewer.parent_ids.
//   • Grandparent / Great-Grandparent  → APPEND target.id to connector.parent_ids.
//   • Sibling / Aunt-Uncle / Cousin / Cousin-Child / Child → MERGE the
//     resolved parents into target.parent_ids (APPEND, never replace).
//   • Spouse → spouse_id link.
//   • Floating modes are NOT permitted here — connect REQUIRES an anchor
//     to be meaningful. We surface the missing-anchor error explicitly.

export interface ConnectExistingParams {
  /** The id of the matched existing person row. */
  existingPersonId: string;
  /** Relationship the user is asserting. */
  relationship: Relationship;
  /** Requested family side, when the calling workflow has one. */
  side?: Side | null;
  /** Governed option key used for creation-policy enforcement. */
  optionKey?: string | null;
  /** Canonical relationship workflow evidence to persist. */
  relationshipWorkflow?: TreeRelationshipWorkflow | null;
  /** Optional descriptive title. Never creates topology. */
  relationshipOverride?: string | null;
  /** Optional secondary anchor for dual-parent links. */
  secondAnchorId?: string | null;
  /** When the edge target is NOT the viewer (e.g. connector for grandparent). */
  anchorOverrideId?: string | null;
}

export interface ConnectExistingResult {
  success: boolean;
  edgeError: string | null;
  databaseError: string | null;
  relationship: Relationship;
  existingPersonId: string;
  anchorId: string | null;
  viewerId: string | null;
  userId: string | null;
  parentIdsApplied: string[];
  edgeVerified: boolean;
  modeApplied: AppliedEdgeMode;
  timestamp: string;
}

export const connectExistingUniversal = async (
  deps: AddRelativeDeps,
  params: ConnectExistingParams,
  atomicCapability?:
    AddRelativeDeps[
      'connectExistingAncestryAtomic'
    ],
): Promise<ConnectExistingResult> => {
  const timestamp = new Date().toISOString();
  const result: ConnectExistingResult = {
    success: false,
    edgeError: null,
    databaseError: null,
    relationship: params.relationship,
    existingPersonId: params.existingPersonId,
    anchorId: null,
    viewerId: deps.viewerMemberId ?? null,
    userId: deps.userId ?? null,
    parentIdsApplied: [],
    edgeVerified: false,
    modeApplied: 'none',
    timestamp,
  };

  const failClosed = (
    message: string,
  ): ConnectExistingResult => {
    result.success = false;
    result.edgeVerified = false;
    result.edgeError = message;
    result.databaseError = message;
    return result;
  };

  const normalizeIds = (
    values:
      readonly string[] | null | undefined,
  ): string[] =>
    Array.from(
      new Set(
        (values ?? [])
          .filter(Boolean),
      ),
    ).sort();

  const normalizeJson = (
    value: unknown,
  ): unknown => {
    if (
      value === null ||
      typeof value !== 'object'
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(
        normalizeJson,
      );
    }

    return Object.fromEntries(
      Object.entries(
        value as Record<string, unknown>,
      )
        .sort(
          ([left], [right]) =>
            left.localeCompare(right),
        )
        .map(
          ([key, child]) => [
            key,
            normalizeJson(child),
          ],
        ),
    );
  };

  const sameIds = (
    left:
      readonly string[] | null | undefined,
    right:
      readonly string[] | null | undefined,
  ): boolean => {
    const a = normalizeIds(left);
    const b = normalizeIds(right);

    return (
      a.length === b.length &&
      a.every(
        (value, index) =>
          value === b[index],
      )
    );
  };

  const sameJson = (
    left: unknown,
    right: unknown,
  ): boolean =>
    JSON.stringify(
      normalizeJson(left),
    ) ===
    JSON.stringify(
      normalizeJson(right),
    );

  // Hard guards — surface the EXACT missing piece.
  if (!deps.userId) {
    result.edgeError = `[${params.relationship}] No logged-in user — cannot connect this profile to your tree.`;
    result.databaseError = result.edgeError;
    return result;
  }
  if (!deps.viewerMemberId) {
    result.edgeError = `[${params.relationship}] Your "You" profile is not linked yet — open your card and confirm "This is me" before connecting an existing profile.`;
    result.databaseError = result.edgeError;
    return result;
  }
  if (!params.relationship) {
    result.edgeError = 'Relationship type is missing — pick how this person is related to you.';
    result.databaseError = result.edgeError;
    return result;
  }

  const creationPolicy =
    resolveCanonicalTreeRelationshipCreationPolicy(
      params.optionKey ?? params.relationship,
    );

  if (creationPolicy.disposition !== 'CREATE') {
    result.edgeError = creationPolicy.reason;
    result.databaseError = creationPolicy.reason;
    return result;
  }

  if (!params.existingPersonId) {
    result.edgeError = `[${params.relationship}] No existing profile selected.`;
    result.databaseError = result.edgeError;
    return result;
  }
  const target = deps.members.find((m) => m.id === params.existingPersonId);
  if (!target) {
    result.edgeError = `[${params.relationship}] The matched profile could not be found in your loaded family graph.`;
    result.databaseError = result.edgeError;
    return result;
  }
  if (target.id === deps.viewerMemberId) {
    result.edgeError = 'That is your own "You" profile — you cannot connect it as a relative of yourself.';
    result.databaseError = result.edgeError;
    return result;
  }

  const workflowKind =
    params.relationshipWorkflow
      ?.kind ?? null;

  if (
    params.relationship ===
      'step-parent' ||
    (
      params.optionKey ??
      ''
    ).startsWith(
      'v_step_',
    ) ||
    workflowKind ===
      'step-family'
  ) {
    return failClosed(
      'Existing-profile step-family connection requires its governed non-ancestral lifecycle writer.',
    );
  }

  if (
    params.relationship ===
      'spouse' ||
    workflowKind ===
      'spouse-partner' ||
    workflowKind ===
      'former-spouse-partner'
  ) {
    return failClosed(
      'Existing-profile spouse or partner connection requires its governed atomic partnership lifecycle writer.',
    );
  }

  // Resolver anchor — for parent / sibling / child the implicit anchor
  // is the viewer when no override was passed.
  const resolverAnchorId = (() => {
    switch (params.relationship) {
      case 'mother':
      case 'father':
      case 'adopted-parent':
      case 'sibling':
      case 'child':
        return params.anchorOverrideId ?? null;
      default:
        return params.anchorOverrideId ?? null;
    }
  })();

  result.anchorId = resolverAnchorId;

  /*
   * CC-TREE-LIVE-003G3A
   *
   * Existing-profile connections receive the same structural generation
   * authorization as new-profile adds before any parent edge is written.
   */
  const generationAnchorDecision =
    resolveCanonicalTreeGenerationAnchorAuthority({
      members:
        deps.members,
      viewerMemberId:
        deps.viewerMemberId,
      relationship:
        params.relationship,
      optionKey:
        params.optionKey ?? null,
      relationshipOverride:
        params.relationshipOverride ??
        null,
      preferredFamilyTitle:
        params.relationshipWorkflow
          ?.preferredFamilyTitle ??
        null,
      anchorMemberId:
        resolverAnchorId,
      subjectMemberId:
        target.id,
      side:
        params.side ??
        null,
    });

  if (
    generationAnchorDecision.applies &&
    !generationAnchorDecision.authorized
  ) {
    result.edgeError =
      generationAnchorDecision.reason;
    result.databaseError =
      generationAnchorDecision.reason;
    return result;
  }

  // Build the merged-by-id snapshot (deps.members is already current —
  // no separate insert happened). Use this for ALL post-write reads.
  const liveById = new Map(deps.members.map((m) => [m.id, m]));

  const edge = resolveEdge(
    deps.members,
    deps.viewerMemberId,
    params.relationship,
    resolverAnchorId,
    params.secondAnchorId ?? null,
  );

  // Helper — safe APPEND, same semantics as addRelativeUniversal.
  const appendParentId = (
    targetMember: GraphMember,
    newParentId: string,
  ): string[] => {
    const existing =
      (targetMember.parentIds ?? [])
        .filter(
          (parentId) =>
            Boolean(parentId) &&
            parentId !== newParentId,
        );
    return Array.from(new Set([newParentId, ...existing]));
  };

  // Locally-computed expected post-write snapshots — used for verification
  // BEFORE the refresh closure becomes available. Independent of React.
  let expectedTargetParents: string[] | null = null;
  let expectedConnectorParents: string[] | null = null;
  let expectedViewerParents: string[] | null = null;
  let expectedSpouseTarget: string | null = null;

  let atomicEdgeMember:
    GraphMember | null = null;

  let atomicProposedParentIds:
    string[] | null = null;

  let atomicMode:
    | 'viewer-parent'
    | 'connector-parent'
    | 'self'
    | null = null;

  let durableAtomic:
    ConnectExistingAncestryAtomicResult | null =
    null;

  const desiredWorkflow =
    params.relationshipWorkflow !==
      undefined
      ? params.relationshipWorkflow
      : target.relationshipWorkflow ??
        null;

  const desiredOverride =
    params.relationshipOverride !==
      undefined
      ? params.relationshipOverride
      : target.relationshipOverride ??
        null;

  const atomicConnectExistingAncestry =
    deps.connectExistingAncestryAtomic ??
    atomicCapability;

  if (
    (
      edge.mode ===
        'viewer-parent' ||
      edge.mode ===
        'connector-parent' ||
      edge.mode ===
        'self'
    ) &&
    !atomicConnectExistingAncestry
  ) {
    return failClosed(
      'Authenticated atomic existing-profile ancestry authority is unavailable.',
    );
  }

  try {
    if (edge.mode === 'viewer-parent') {
      result.modeApplied = 'viewer-parent';
      const viewer = liveById.get(deps.viewerMemberId);
      if (!viewer) {
        result.edgeError = `[${params.relationship}] Your "You" profile could not be loaded — refresh and try again.`;
        result.databaseError = result.edgeError;
        return result;
      }
      const next =
        appendParentId(
          viewer,
          target.id,
        );

      atomicEdgeMember =
        viewer;

      atomicProposedParentIds =
        normalizeIds(next);

      atomicMode =
        'viewer-parent';

      result.parentIdsApplied =
        next;

      expectedViewerParents =
        next;
    } else if (edge.mode === 'connector-parent') {
      result.modeApplied = 'connector-parent';

      /*
       * Existing people are never attached as ancestors through a raw
       * parent_ids append. Reuse the canonical ancestry-reconnection
       * planner so self-parent, cross-graph, and cycle protections remain
       * mandatory at the mutation boundary.
       */
      const ancestryPlan =
        planCanonicalTreeAncestryReconnection({
          members:
            deps.members,
          subjectMemberId:
            target.id,
          intent:
            'ADD_SUBJECT_AS_PARENT',
          anchorMemberId:
            edge.connectorId,
        });

      if (
        !ancestryPlan.authorized ||
        ancestryPlan.status !==
          'AUTHORIZED_PARENT_EDGE_PLAN'
      ) {
        result.edgeError =
          ancestryPlan.reason;
        result.databaseError =
          ancestryPlan.reason;
        return result;
      }

      const connector =
        liveById.get(
          ancestryPlan
            .parentEdgePlan
            .targetMemberId,
        ) ?? null;

      if (!connector) {
        return failClosed(
          'The governed ancestry edge target is missing from the loaded graph.',
        );
      }

      const nextParents =
        normalizeIds([
          ...(
            connector.parentIds ??
            []
          ),
          ...ancestryPlan
            .parentEdgePlan
            .proposedParentIds,
        ]);

      atomicEdgeMember =
        connector;

      atomicProposedParentIds =
        nextParents;

      atomicMode =
        'connector-parent';

      result.parentIdsApplied =
        nextParents;

      expectedConnectorParents =
        nextParents;
    } else if (edge.mode === 'spouse') {
      result.modeApplied = 'spouse';
      return failClosed(
        'Partnership mutation is outside the authenticated ancestry writer.',
      );
    } else if (edge.mode === 'self') {
      result.modeApplied = 'self';
      // Existing person inherits the resolved parent_ids
      // (sibling / aunt-uncle / cousin / child). APPEND — never replace.
      const targetExisting =
        (target.parentIds ?? [])
          .filter(Boolean);

      const merged = Array.from(
        new Set([
          ...edge.parents.filter(Boolean),
          ...targetExisting,
        ]),
      );

      atomicEdgeMember =
        target;

      atomicProposedParentIds =
        normalizeIds(merged);

      atomicMode =
        'self';

      result.parentIdsApplied = merged;
      expectedTargetParents = merged;
    } else {
      // 'floating' — connect REQUIRES an anchor to be meaningful.
      result.modeApplied = 'floating';
      result.edgeError = `[${params.relationship}] ${edge.reason}`;
      result.databaseError = result.edgeError;
      return result;
    }
  } catch (err: any) {
    const msg =
      err?.message ||
      err?.error_description ||
      err?.toString?.() ||
      `Unknown database error while writing the ${params.relationship} edge.`;
    result.edgeError = `[${params.relationship}] ${msg}`;
    result.databaseError = msg;
    return result;
  }

  if (
    atomicEdgeMember &&
    atomicProposedParentIds &&
    atomicMode
  ) {
    const expectedParentIds =
      normalizeIds(
        atomicEdgeMember
          .parentIds ??
          [],
      );

    if (
      !expectedParentIds.every(
        parentId =>
          atomicProposedParentIds!
            .includes(
              parentId,
            ),
      )
    ) {
      return failClosed(
        'Additive ancestry authority refused a proposed state that removes an existing parent reference.',
      );
    }

    try {
      durableAtomic =
        await atomicConnectExistingAncestry!({
            viewerMemberId:
            deps.viewerMemberId,

          relationship:
            params.relationship,

          requestedSide:
            params.side ??
            null,

          anchorMemberId:
            resolverAnchorId,

          secondAnchorMemberId:
            params.secondAnchorId ??
            null,

          edgeMemberId:
              atomicEdgeMember.id,

            evidenceMemberId:
              target.id,

            expectedParentIds,

            proposedParentIds:
              atomicProposedParentIds,

            expectedRelationshipWorkflow:
              target
                .relationshipWorkflow ??
              null,

            relationshipWorkflow:
              desiredWorkflow,

            expectedRelationshipOverride:
              target
                .relationshipOverride ??
              null,

            relationshipOverride:
              desiredOverride,

            relationshipKey:
              params.optionKey ??
              params.relationship,

            modeApplied:
              atomicMode,
          });
    } catch (error: any) {
      const message =
        error?.message ||
        error?.error_description ||
        error?.toString?.() ||
        'Atomic ancestry transaction failed.';

      return failClosed(
        '[' +
          params.relationship +
          '] ' +
          message,
      );
    }

    const identityVerified =
      durableAtomic.edgeMemberId ===
        atomicEdgeMember.id &&
      durableAtomic.evidenceMemberId ===
        target.id;

    const parentsVerified =
      identityVerified &&
      sameIds(
        durableAtomic.parentIds,
        atomicProposedParentIds,
      );

    const workflowVerified =
      identityVerified &&
      sameJson(
        durableAtomic
          .relationshipWorkflow,
        desiredWorkflow,
      );

    const overrideVerified =
      identityVerified &&
      durableAtomic
        .relationshipOverride ===
        desiredOverride;

    const auditVerified =
      identityVerified &&
      durableAtomic.status ===
        'connected' &&
      typeof durableAtomic.auditId ===
        'string' &&
      durableAtomic.auditId.length >
        0;

    result.parentIdsApplied =
      Array.isArray(
        durableAtomic.parentIds,
      )
        ? [
            ...durableAtomic.parentIds,
          ]
        : [];

    result.edgeVerified =
      Boolean(
        parentsVerified &&
        workflowVerified &&
        overrideVerified &&
        auditVerified,
      );

    if (!result.edgeVerified) {
      return failClosed(
        'Atomic ancestry writer returned a durable post-state that does not exactly match the authorized transaction.',
      );
    }
  }

  // Persist governed relationship evidence without changing the
  // existing person's identity, claim, name, birthday, photo,
  // parent list, or spouse field beyond the explicit edge above.
  if (
    !durableAtomic &&
    params.relationshipWorkflow &&
    deps.updateMember
  ) {
    try {
      const updates: Partial<GraphMember> = {
        relationshipWorkflow:
          params.relationshipWorkflow,
      };

      if (params.relationshipOverride !== undefined) {
        updates.relationshipOverride =
          params.relationshipOverride;
      }

      await deps.updateMember(target.id, updates);
    } catch (err: any) {
      const msg =
        err?.message ||
        err?.error_description ||
        err?.toString?.() ||
        'Could not persist relationship workflow evidence.';

      result.edgeError =
        `[${params.relationship}] ${msg}`;
      result.databaseError = msg;
      return result;
    }
  }

  // Refresh.
  try {
    await deps.refresh();
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.warn('[connectExistingUniversal] post-save refresh failed (non-fatal)', err);
  }

  /*
   * Mutation success now comes only from the durable transaction return.
   * Refresh is presentation synchronization, not write verification.
   */
  if (
    !durableAtomic ||
    !result.edgeVerified
  ) {
    return failClosed(
      'Atomic existing-profile ancestry verification is unavailable.',
    );
  }

  result.success = true;
  return result;
};
